import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, KeyRound, Loader2, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../lib/api.js";
import { authClient } from "../lib/auth-client.js";

interface MeUser {
  id: string;
  email: string;
  name: string | null;
  image: string | null;
}
interface MeOrg {
  id: string;
  name: string;
  role: string;
}
interface Me {
  user: MeUser;
  organization: MeOrg | null;
  organizations: MeOrg[];
}

interface PresignResponse {
  url: string;
  s3Key: string;
  headers: Record<string, string>;
  publicUrl: string;
  expiresIn: number;
}

const AVATAR_MAX_BYTES = 4 * 1024 * 1024;
const AVATAR_ALLOWED = /^image\/(png|jpe?g|webp|avif)$/i;

/**
 * Account / profile page.
 *
 * Three glass-floating cards stacked on a single column:
 *
 *   1. Identity   — avatar (with upload + clear) and display-name editor.
 *   2. Membership — read-only email, active club, role.
 *   3. Password   — change-password form.
 *
 * Club-level data (logo, name, storage quota) lives on the separate
 * `/club` page.
 *
 * Avatar upload flow: ask `/api/v1/media/avatar-upload-url` for a presigned
 * PUT (gated by the org quota server-side), PUT the bytes straight to
 * MinIO, then PATCH `/api/v1/me/profile` with `{ avatar: { s3Key, … } }` so
 * the server can register a `MediaRef` row and update `user.image` via
 * Better Auth.
 */
export function AccountPage() {
  const qc = useQueryClient();

  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => apiFetch<Me>("/api/v1/me"),
  });

  const patchProfile = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<{ ok: boolean; image: string | null }>("/api/v1/me/profile", {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["me"] });
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <IdentityCard
        me={me.data}
        loading={me.isLoading}
        saving={patchProfile.isPending}
        onSaveName={(name) => patchProfile.mutateAsync({ name })}
        onChangeAvatar={async (avatar) => {
          await patchProfile.mutateAsync({ avatar });
        }}
        onClearAvatar={() => patchProfile.mutateAsync({ image: null })}
      />
      <MembershipCard me={me.data} />
      <PasswordCard />
    </div>
  );
}

/* ─────────────────────── Identity card ───────────────────────── */

function IdentityCard({
  me,
  loading,
  saving,
  onSaveName,
  onChangeAvatar,
  onClearAvatar,
}: {
  me: Me | undefined;
  loading: boolean;
  saving: boolean;
  onSaveName: (name: string) => Promise<unknown>;
  onChangeAvatar: (avatar: {
    s3Key: string;
    publicUrl: string;
    bytes: number;
    mime: string;
  }) => Promise<unknown>;
  onClearAvatar: () => Promise<unknown>;
}) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [name, setName] = useState("");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  // Sync local edit buffer with server state whenever /me resolves. We don't
  // want server refetches to stomp on an in-progress edit, so only sync
  // when the buffer is still empty.
  useEffect(() => {
    if (me && !name) setName(me.user.name ?? "");
  }, [me, name]);

  const initials = computeInitials(me?.user.name ?? null, me?.user.email ?? "");
  const dirty = !!me && name.trim() !== (me.user.name ?? "").trim() && name.trim().length > 0;

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!AVATAR_ALLOWED.test(file.type)) {
      setMsg({ kind: "err", text: "Nur PNG, JPG, WebP oder AVIF." });
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setMsg({ kind: "err", text: "Maximal 4 MB." });
      return;
    }

    setUploadBusy(true);
    setMsg(null);
    try {
      const presign = await apiFetch<PresignResponse>("/api/v1/media/avatar-upload-url", {
        method: "POST",
        body: JSON.stringify({ mime: file.type, bytes: file.size }),
      });

      const put = await fetch(presign.url, {
        method: "PUT",
        headers: presign.headers,
        body: file,
      });
      if (!put.ok) throw new Error(`PUT failed: ${put.status}`);

      await onChangeAvatar({
        s3Key: presign.s3Key,
        publicUrl: presign.publicUrl,
        bytes: file.size,
        mime: file.type,
      });
      setMsg({ kind: "ok", text: "Profilbild aktualisiert." });
    } catch (err) {
      const text =
        err instanceof Error && err.message.includes("413")
          ? "Speicherlimit erreicht. Lösche zuerst andere Medien."
          : "Upload fehlgeschlagen.";
      setMsg({ kind: "err", text });
    } finally {
      setUploadBusy(false);
    }
  }

  return (
    <Card>
      <h2 className="home-display text-[18px] font-bold" style={{ color: "#f4f5f3" }}>
        Profil
      </h2>

      <div className="mt-6 flex flex-col items-center gap-6 sm:flex-row sm:items-center sm:gap-7">
        <div className="relative shrink-0">
          <div
            className="grid size-24 place-items-center overflow-hidden rounded-full"
            style={{
              background: me?.user.image
                ? "transparent"
                : "linear-gradient(140deg, rgba(151,252,0,0.45) 0%, rgba(151,252,0,0.10) 100%)",
              border: "1px solid rgba(255,255,255,0.10)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.20)",
            }}
          >
            {me?.user.image ? (
              <img src={me.user.image} alt="" className="size-24 rounded-full object-cover" />
            ) : (
              <span
                className="home-display"
                style={{ fontSize: 30, fontWeight: 800, color: "#1d3700" }}
              >
                {initials}
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploadBusy || saving}
            aria-label="Profilbild ändern"
            className="absolute bottom-0 right-0 grid size-9 place-items-center rounded-full border transition-colors duration-fast disabled:opacity-60"
            style={{
              background: "var(--color-pitch-lime)",
              color: "#1d3700",
              borderColor: "rgba(10,12,8,0.45)",
              boxShadow: "0 6px 16px -4px rgba(151,252,0,0.45)",
            }}
          >
            {uploadBusy ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Camera className="size-4" />
            )}
          </button>

          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/avif"
            className="hidden"
            onChange={handleFile}
          />
        </div>

        <div className="min-w-0 flex-1 self-stretch">
          <label
            htmlFor="account-name"
            className="text-[11px] uppercase tracking-[0.14em]"
            style={{ color: "var(--color-on-surface-variant)" }}
          >
            Anzeigename
          </label>
          <input
            id="account-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={loading ? "…" : "Dein Name"}
            maxLength={100}
            className="mt-2 w-full rounded-xl border bg-transparent px-3.5 py-2.5 text-[15px] outline-none transition-colors focus:border-pitch-lime"
            style={{
              borderColor: "rgba(255,255,255,0.12)",
              color: "#f4f5f3",
            }}
          />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
        {me?.user.image && (
          <button
            type="button"
            onClick={() => {
              setMsg(null);
              void onClearAvatar().then(() => setMsg({ kind: "ok", text: "Profilbild entfernt." }));
            }}
            disabled={saving || uploadBusy}
            className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-[13px] transition-colors disabled:opacity-60"
            style={{
              borderColor: "rgba(255,255,255,0.10)",
              color: "var(--color-on-surface-variant)",
            }}
          >
            <Trash2 className="size-4" /> Bild entfernen
          </button>
        )}
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={() => {
            setMsg(null);
            void onSaveName(name.trim()).then(() =>
              setMsg({ kind: "ok", text: "Name gespeichert." }),
            );
          }}
          className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-[13px] font-semibold transition-colors disabled:opacity-50"
          style={{
            background: "var(--color-pitch-lime)",
            color: "#1d3700",
          }}
        >
          {saving ? <Loader2 className="size-4 animate-spin" /> : null}
          Speichern
        </button>
      </div>

      {msg && (
        <p
          className="mt-3 text-[12px]"
          style={{
            color: msg.kind === "ok" ? "var(--color-pitch-lime)" : "var(--color-error, #ff6b6b)",
          }}
        >
          {msg.text}
        </p>
      )}
    </Card>
  );
}

/* ──────────────────── Membership / email card ─────────────────────── */

function MembershipCard({ me }: { me: Me | undefined }) {
  return (
    <Card>
      <h2 className="home-display text-[18px] font-bold" style={{ color: "#f4f5f3" }}>
        Konto
      </h2>
      <dl className="mt-4 grid gap-3 text-[14px]">
        <Row label="E-Mail" value={me?.user.email ?? "—"} />
        <Row label="Aktueller Verein" value={me?.organization?.name ?? "—"} />
        <Row label="Rolle" value={me?.organization ? roleLabel(me.organization.role) : "—"} />
      </dl>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt
        className="text-[11px] uppercase tracking-[0.14em]"
        style={{ color: "var(--color-on-surface-variant)" }}
      >
        {label}
      </dt>
      <dd className="truncate text-right" style={{ color: "#f4f5f3" }}>
        {value}
      </dd>
    </div>
  );
}

function roleLabel(role: string): string {
  switch (role) {
    case "owner":
      return "Inhaber";
    case "admin":
      return "Admin";
    case "coach":
      return "Trainer";
    default:
      return role;
  }
}

/* ─────────────────────── Password card ─────────────────────────── */

/**
 * Lets the signed-in user change their password via Better Auth's
 * `changePassword` endpoint. Requires the current password (server-side
 * verification), enforces an 8-character minimum and a matching confirm
 * field client-side, and offers to revoke all other sessions after a
 * successful change.
 */
function PasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [revokeOthers, setRevokeOthers] = useState(true);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const change = useMutation({
    mutationFn: async () => {
      const res = await authClient.changePassword({
        currentPassword: current,
        newPassword: next,
        revokeOtherSessions: revokeOthers,
      });
      if (res.error) throw new Error(res.error.message ?? "change failed");
      return res.data;
    },
    onSuccess: () => {
      setMsg({ kind: "ok", text: "Passwort aktualisiert." });
      setCurrent("");
      setNext("");
      setConfirm("");
    },
    onError: (err) => {
      const raw = err instanceof Error ? err.message.toLowerCase() : "";
      const text =
        raw.includes("invalid") || raw.includes("incorrect")
          ? "Aktuelles Passwort ist falsch."
          : "Passwort konnte nicht geändert werden.";
      setMsg({ kind: "err", text });
    },
  });

  const tooShort = next.length > 0 && next.length < 8;
  const mismatch = confirm.length > 0 && confirm !== next;
  const canSubmit = !change.isPending && current.length > 0 && next.length >= 8 && confirm === next;

  return (
    <Card>
      <div className="flex items-center gap-3">
        <KeyRound size={18} style={{ color: "var(--color-on-surface-variant)" }} aria-hidden />
        <h2 className="home-display text-[18px] font-bold" style={{ color: "#f4f5f3" }}>
          Passwort ändern
        </h2>
      </div>
      <p
        className="mt-1 text-[12px] leading-relaxed"
        style={{ color: "var(--color-on-surface-variant)" }}
      >
        Mindestens 8 Zeichen. Du wirst hier nicht abgemeldet; andere Geräte können optional
        automatisch ausgeloggt werden.
      </p>

      <form
        className="mt-5 flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          setMsg(null);
          change.mutate();
        }}
      >
        <PasswordField
          label="Aktuelles Passwort"
          autoComplete="current-password"
          value={current}
          onChange={setCurrent}
        />
        <PasswordField
          label="Neues Passwort"
          autoComplete="new-password"
          value={next}
          onChange={setNext}
          hint={tooShort ? "Mindestens 8 Zeichen." : undefined}
          invalid={tooShort}
        />
        <PasswordField
          label="Neues Passwort bestätigen"
          autoComplete="new-password"
          value={confirm}
          onChange={setConfirm}
          hint={mismatch ? "Passwörter stimmen nicht überein." : undefined}
          invalid={mismatch}
        />

        <label
          className="mt-1 flex items-center gap-2 text-[13px]"
          style={{ color: "var(--color-on-surface-variant)" }}
        >
          <input
            type="checkbox"
            checked={revokeOthers}
            onChange={(e) => setRevokeOthers(e.target.checked)}
            className="h-4 w-4 accent-(--color-pitch-lime)"
          />
          Andere Sitzungen abmelden
        </label>

        <div className="mt-2 flex items-center gap-3">
          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-[13px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              background: "var(--color-pitch-lime)",
              color: "#0a0c0c",
            }}
          >
            {change.isPending ? (
              <>
                <Loader2 size={14} className="animate-spin" aria-hidden />
                Speichern …
              </>
            ) : (
              "Passwort aktualisieren"
            )}
          </button>
          {msg && (
            <span
              className="text-[12px]"
              style={{
                color: msg.kind === "ok" ? "var(--color-pitch-lime)" : "#ff8a8a",
              }}
              role="status"
            >
              {msg.text}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

function PasswordField({
  label,
  value,
  onChange,
  autoComplete,
  hint,
  invalid,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: string;
  hint?: string;
  invalid?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span
        className="text-[11px] uppercase tracking-[0.14em]"
        style={{ color: "var(--color-on-surface-variant)" }}
      >
        {label}
      </span>
      <input
        type="password"
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-2xl px-4 py-2 text-[14px] outline-none transition"
        style={{
          background: "rgba(255,255,255,0.06)",
          color: "#f4f5f3",
          border: invalid ? "1px solid #ff8a8a" : "1px solid rgba(255,255,255,0.08)",
        }}
      />
      {hint && (
        <span className="text-[11px]" style={{ color: "#ff8a8a" }}>
          {hint}
        </span>
      )}
    </label>
  );
}

/* ──────────────────────── building blocks ─────────────────────── */

function Card({ children }: { children: React.ReactNode }) {
  return (
    <section className="glass-floating rounded-3xl px-6 py-7 sm:px-8 sm:py-8">{children}</section>
  );
}

function computeInitials(name: string | null, email: string): string {
  const source = (name?.trim() || email.split("@")[0] || "?").trim();
  const parts = source.split(/\s+/).filter(Boolean).slice(0, 2);
  const letters = parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
  return letters || source[0]?.toUpperCase() || "?";
}
